/**
 * Links from the business app into the player app, which hosts the chrome-less
 * venue display and the entrance scan kiosk.
 *
 * Only known business origins map to a player origin. Unknown origins return
 * null rather than guessing a host that could send a one-time credential to
 * the wrong app.
 */
export function playerOriginForBusinessOrigin(
  businessOrigin: string,
): string | null {
  let source: URL;
  try {
    source = new URL(businessOrigin);
  } catch {
    return null;
  }
  const host = source.hostname;
  const preview = /^pr-(\d+)-business\.badmintonwarriors\.com$/.exec(host);
  if (preview) return `https://pr-${preview[1]}-player.badmintonwarriors.com`;
  if (host === "staging-business.badmintonwarriors.com") {
    return "https://staging.badmintonwarriors.com";
  }
  if (host === "business.badmintonwarriors.com")
    return "https://badmintonwarriors.com";
  if (host === "localhost" || host === "127.0.0.1") {
    return `${source.protocol}//${host}:3000`;
  }
  return null;
}

/**
 * The pairing link for a kiosk setup code (#2691): the player app's `/kiosk`
 * with the code in the fragment. A setup code is a one-time bearer credential;
 * fragments stay out of the HTTP request and Referer header, and the kiosk
 * page scrubs it from history after reading it.
 */
export function buildKioskSetupUrl(
  businessOrigin: string,
  setupCode: string,
): string | null {
  if (!setupCode.trim()) return null;
  const playerOrigin = playerOriginForBusinessOrigin(businessOrigin);
  if (!playerOrigin) return null;
  const target = new URL("/kiosk", playerOrigin);
  target.hash = new URLSearchParams({ code: setupCode }).toString();
  return target.toString();
}
